/**
 * 1.66 (192) — the till's café slice: the pure pieces the store uses around the table functions' replies.
 */
import type { Key } from './i18n'
import { tables as tablesKa } from './i18n/ka/tables'
import { parseServicePct } from './service-charge'
import type { PingStation } from './tables'
import { ringKey, type GuestRequest } from './table-requests'

// 196 (1.68) adds the service product's two refusals and the waiter numbers' two; 1.69 (197/198) the guests' requests,
// the QR tokens and the reservation
const CODE = /\b(TABLE_[A-Z_]+|PRODUCT_ON_OPEN_BILL|SERVICE_CHARGE_NOT_HERE|SERVICE_PRODUCT_LOCKED|WAITER_STATS_[A-Z_]+|REQUEST_[A-Z_]+|QR_OWNER_ONLY|RESERVE_INVALID)\b/

/** 1.70 — the refusal's code itself (`REQUEST_ADDON_INVALID`, `TABLE_…`), or null when it is not a table refusal. */
export function tableErrorCode(message: string): string | null {
  return CODE.exec(message)?.[1] ?? null
}

/** The server's refusal as a key of the till's dictionary, or null when it is not a table refusal we know. */
export function tableErrorKey(message: string): Key | null {
  const m = CODE.exec(message)
  if (!m) return null
  const key = `tables.err.${m[1]}`
  return key in tablesKa ? (key as Key) : null
}

const RELOAD = new Set(['TABLE_BILL_CLOSED', 'TABLE_BILL_CHANGED', 'TABLE_LINES_MISMATCH', 'TABLE_BUSY', 'TABLE_NOT_FOUND',
  // 196 (1.68): a bill opened on the table being freed, a request or a bill changed under the check, the service line
  'TABLE_HAS_OPEN_BILL', 'TABLE_CHECK_STALE', 'TABLE_CHECK_SNAPSHOT', 'TABLE_SERVICE_MISMATCH',
  // 1.69: a move onto a table that is taken (a party, a seated payment, a reservation) — the move list was old
  'TABLE_OCCUPIED'])

/** The refusal says the till's copy of a bill is old: read the bills again. */
export function tablesNeedReload(message: string): boolean {
  const m = CODE.exec(message)
  return !!m && RELOAD.has(m[1])
}

export type ReplyStamp = { gen: number; orgId: string | null | undefined }

/** A reply lands only in the session (boot generation) and the shop it was asked for. */
export function acceptsReply(stamp: ReplyStamp, current: ReplyStamp): boolean {
  return stamp.gen === current.gen && stamp.orgId === current.orgId
}

/** A load that no longer lists a bill marks it gone at that load's sequence number (paid or cancelled elsewhere). */
export function tombstoneAfterLoad(prevIds: readonly string[], nextIds: readonly string[], seq: number, gone: Map<string, number>): void {
  const next = new Set(nextIds)
  for (const id of prevIds) if (!next.has(id)) gone.set(id, seq)
}

/** A reply belongs on screen unless its bill left the list after the call started (Codex final #5). */
export function acceptBillReply(gone: ReadonlyMap<string, number>, billId: string, startSeq: number): boolean {
  const g = gone.get(billId)
  return g === undefined || startSeq >= g
}

const DOUBT_MS = 120_000

/**
 * The op id a table call sends. A press made after the same call ended in DOUBT (a lost answer) gets that call's op
 * again, so the server answers the first result instead of acting twice (Codex final #6). Anything else is a new op:
 * two quick taps of one product while the first is in flight are two coffees; a doubt older than two minutes is a new
 * press.
 */
export function pendingOp(pending: Map<string, { op: string; at: number }>, key: string, now: number, mint: () => string): string {
  const had = pending.get(key)
  if (had && now - had.at <= DOUBT_MS) return had.op
  return mint()
}

/** 'done' — the server answered (a result or a refusal): forget the op; 'doubt' — keep it for the next press. */
export function settleOp(pending: Map<string, { op: string; at: number }>, key: string, outcome: 'done' | 'doubt', op: string, now: number): void {
  if (outcome === 'done') pending.delete(key)
  else pending.set(key, { op, at: now })
}

// ── 1.68 (196) ──

/**
 * The shop's service product (spec G7) never reaches a screen's product list: the marked rows are kept apart, and the
 * oldest is the shop's — the server's own pick (`private.table_service_product`: created_at, then id). Kept, not
 * dropped: the product copy and the delta's count (`list_products_changed.total`) still hold them. Nothing marked →
 * the very same array, so a list that did not change neither re-renders nor re-saves.
 */
export function splitServiceProducts<T extends { id: string; system_kind?: string | null; created_at?: string }>(
  rows: readonly T[],
): { products: T[]; service: T[]; serviceId: string | null } {
  if (!rows.some((r) => r.system_kind)) return { products: rows as T[], service: [], serviceId: null }
  const products: T[] = []
  const service: T[] = []
  for (const r of rows) (r.system_kind ? service : products).push(r)
  const at = (r: T) => Date.parse(r.created_at ?? '') || 0
  const oldest = [...service].sort((a, b) =>
    at(a) - at(b) || (a.created_at ?? '').localeCompare(b.created_at ?? '') || a.id.localeCompare(b.id))[0]
  return { products, service, serviceId: oldest.id }
}

/** This device's café duties (spec G11): the bar's queue and the waiters' checks. Saved with the shop and the branch
 *  they were chosen for — on another shop or branch the device has none. */
export type TillDuties = { orgId: string; branchId: string; bar: boolean; checks: boolean }

export function tillDutiesFor(
  d: TillDuties | null | undefined, orgId: string | null | undefined, branchId: string | null | undefined,
): { bar: boolean; checks: boolean } {
  if (!d || !orgId || !branchId || d.orgId !== orgId || d.branchId !== branchId) return { bar: false, checks: false }
  return { bar: d.bar === true, checks: d.checks === true }
}

/** The café's two settings, one reader for the start and the live refresh. The rate is written only by
 *  `table_service_set` (it makes the service product), the switch only by its own action — never `updateSettings`. */
export function tableSettingsFromKv(kv: Record<string, string | undefined>): { tableServicePct: number; tableFreeOnPay: boolean } {
  return { tableServicePct: parseServicePct(kv['table.service_pct']), tableFreeOnPay: kv['table.free_on_pay'] === 'true' }
}

/** A device with the check duty was seen in the last three minutes (`kitchen_stations_online`). Unknown (the read
 *  failed) never alarms the waiter — the request is recorded either way and the till answers it by hand. */
export function checkPrinterOnline(online: readonly PingStation[] | null): boolean {
  return online === null || online.includes('check')
}

const STATION_DEVICE_KEY = 'martepos:kitchen-device'

/** This device's id for the station heartbeat and the check claims (`kitchen_screens.device_id`, a uuid) — the kitchen
 *  board's key since 1.67, so one device keeps one heartbeat row whatever it does. Blocked storage → a fresh uuid. */
export function stationDeviceId(): string {
  try {
    const had = localStorage.getItem(STATION_DEVICE_KEY)
    if (had) return had
    const id = crypto.randomUUID()
    localStorage.setItem(STATION_DEVICE_KEY, id)
    return id
  } catch { return crypto.randomUUID() }
}

/** A branch switch (Codex final #6): the seated list is the old branch's — drop it, and read the tables again when this
 *  device has them open, or the new branch's paid tables show free until the next refresh. 1.69: the parties at the
 *  tables and the guests' requests are the branch's too. */
export function branchSwitchTables(bills: object | null): { patch: { seated: null; visits: null; guestRequests: null }; reload: boolean } {
  return { patch: { seated: null, visits: null, guestRequests: null }, reload: bills !== null }
}

// ── 1.69 (197/198) — the guests' requests and the parties at the tables ──

export type ReadPlace = { gen: number; orgId: string | null | undefined; branchId: string | null | undefined }

/** A read's answer lands only in the session, shop and branch it was asked for, and only when no newer read of the same
 *  slice started meanwhile (`latestOnly`, the 1.65 pattern). */
export function acceptsRead(stamp: ReadPlace & { k: number }, current: ReadPlace, isLatest: (k: number) => boolean): boolean {
  return stamp.gen === current.gen && stamp.orgId === current.orgId && stamp.branchId === current.branchId && isLatest(stamp.k)
}

/** Which requests chime now: a waiting one this device would act on (a waiter: his own party's or nobody's; a till:
 *  all) whose ring (`ringKey`: the request and its last ring) this device has not seen. The first read after the device
 *  starts listening is the baseline — what was already waiting shows in the list but does not chime (as the online
 *  orders' alerts). */
export function ringsToChime(seen: ReadonlySet<string> | null, reqs: readonly GuestRequest[], isTill: boolean): {
  chime: GuestRequest[]; seen: Set<string>
} {
  const ringing = reqs.filter((r) => r.status === 'waiting' && (isTill || r.audience !== 'other'))
  const next = new Set(ringing.map(ringKey))
  if (seen === null) return { chime: [], seen: next }
  return { chime: ringing.filter((r) => !seen.has(ringKey(r))), seen: new Set([...seen, ...next]) }
}

